import * as React from "react";
import { Link } from "wouter";
import { AlertCircle, ArrowLeft } from "lucide-react";

import { cn } from "@/lib/cn";
import { Button } from "@/components/ui/button";

/**
 * NotFoundState
 *
 * Canonical reference: ianfiles/ui-design-system.md §2.12 + §3.6
 *
 * Centred empty state for missing entities. Render this when a detail page's
 * query returns null — do **not** render an empty page or redirect.
 *
 *   <NotFoundState entityType="Order" backPath="/orders" />
 *
 * Standardised so the back button, copy, and icon size stay consistent
 * across the app. Test id is auto-derived from `entityType` so QA can
 * target the empty branch without per-page configuration.
 */
export interface NotFoundStateProps {
  /** The entity noun rendered in the heading, e.g. "Order", "Account" */
  entityType: string;
  /** Where the Back button should navigate. Pass undefined to hide it. */
  backPath?: string;
  /** Override the heading copy. Defaults to `{entityType} not found`. */
  message?: React.ReactNode;
  /** Optional helper text rendered under the heading. */
  description?: React.ReactNode;
  className?: string;
}

export function NotFoundState({
  entityType,
  backPath,
  message,
  description,
  className,
}: NotFoundStateProps) {
  const slug = entityType.toLowerCase().replace(/\s+/g, "-");
  return (
    <div
      data-testid={`not-found-${slug}`}
      className={cn(
        "flex flex-col items-center justify-center text-center",
        "min-h-[400px] gap-4 p-6",
        className
      )}
    >
      <AlertCircle className="h-12 w-12 text-muted-foreground" />
      <div className="space-y-1">
        <p className="text-lg text-muted-foreground">
          {message ?? `${entityType} not found`}
        </p>
        {description ? (
          <p className="text-sm text-muted-foreground/80">{description}</p>
        ) : null}
      </div>
      {backPath ? (
        <Button variant="outline" asChild data-testid={`button-back-${slug}`}>
          <Link href={backPath}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Link>
        </Button>
      ) : null}
    </div>
  );
}
